const PURE_READ_RETRY_DELAY_MS = 400;
const PURE_READ_RETRY_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);
const PURE_READ_METHODS = new Set(['GET', 'HEAD']);

export type RequestTimeoutOptions = {
  signal?: AbortSignal;
  timeoutMs?: number;
  timeoutMessage?: string;
};

type PureReadRequestInit = Omit<RequestInit, 'body' | 'method'> & {
  method?: 'GET' | 'HEAD';
};

type PureReadFetchOptions = RequestTimeoutOptions & {
  retryDelayMs?: number;
  retryStatuses?: ReadonlySet<number>;
  maxRetryDelayMs?: number;
};

export class FetchRequestTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FetchRequestTimeoutError';
  }
}

function parseRetryAfterMs(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const seconds = Number.parseFloat(trimmed);
  if (Number.isFinite(seconds) && seconds > 0) {
    return Math.floor(seconds * 1_000);
  }

  const timestamp = Date.parse(trimmed);
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  const diffMs = timestamp - Date.now();
  if (diffMs <= 0) {
    return null;
  }

  return Math.floor(diffMs);
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

function isTransientNetworkError(error: unknown): boolean {
  if (isAbortError(error)) {
    return false;
  }

  if (error instanceof FetchRequestTimeoutError) {
    return true;
  }

  if (error instanceof TypeError) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  const normalizedMessage = error.message.trim().toLowerCase();
  if (!normalizedMessage) {
    return false;
  }

  return (
    normalizedMessage.includes('failed to fetch') ||
    normalizedMessage.includes('network') ||
    normalizedMessage.includes('load failed') ||
    normalizedMessage.includes('fetch failed')
  );
}

function sleep(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const delayMs = Math.max(0, Math.floor(ms));
    const timer = globalThis.setTimeout(() => {
      cleanup();
      resolve();
    }, delayMs);

    const cleanup = () => {
      globalThis.clearTimeout(timer);
      signal?.removeEventListener('abort', handleAbort);
    };

    const handleAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    if (signal?.aborted) {
      handleAbort();
      return;
    }

    signal?.addEventListener('abort', handleAbort, { once: true });
  });
}

function withAttemptSignal(params: {
  signal?: AbortSignal;
  timeoutMs?: number;
}) {
  const controller = new AbortController();
  let timedOut = false;
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null;
  let rejectAbort: (error: unknown) => void = () => {};
  const abortPromise = new Promise<never>((_, reject) => {
    rejectAbort = reject;
  });

  const abortFromParent = () => {
    const reason = params.signal?.reason;
    controller.abort(reason);
    rejectAbort(
      reason instanceof Error
        ? reason
        : new DOMException('Aborted', 'AbortError')
    );
  };

  if (params.signal?.aborted) {
    controller.abort();
  } else {
    params.signal?.addEventListener('abort', abortFromParent, { once: true });
  }

  if (
    typeof params.timeoutMs === 'number' &&
    Number.isFinite(params.timeoutMs) &&
    params.timeoutMs > 0
  ) {
    timeoutId = globalThis.setTimeout(() => {
      timedOut = true;
      controller.abort();
      rejectAbort(new DOMException('Aborted', 'AbortError'));
    }, Math.floor(params.timeoutMs));
  }

  return {
    signal: controller.signal,
    abortPromise,
    didTimeout() {
      return timedOut;
    },
    cleanup() {
      if (timeoutId !== null) {
        globalThis.clearTimeout(timeoutId);
      }
      params.signal?.removeEventListener('abort', abortFromParent);
    },
  };
}

function getMethod(init?: RequestInit) {
  return (init?.method ?? 'GET').toUpperCase();
}

async function fetchOnce(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  options?: RequestTimeoutOptions
): Promise<Response> {
  const signal: AbortSignal | undefined =
    options?.signal ?? init?.signal ?? undefined;
  return executeRequestWithTimeout(
    (attemptSignal) =>
      fetch(input, {
        ...init,
        signal: attemptSignal,
      }),
    {
      ...options,
      signal,
    }
  );
}

/**
 * Runs exactly one external request attempt with a caller-owned timeout.
 * It deliberately does not retry; callers must opt into a retry policy only
 * when the operation is known to be side-effect free.
 */
export async function executeRequestWithTimeout<T>(
  executor: (signal: AbortSignal) => Promise<T>,
  options?: RequestTimeoutOptions
): Promise<T> {
  const attemptSignal = withAttemptSignal({
    signal: options?.signal,
    timeoutMs: options?.timeoutMs,
  });

  try {
    return await Promise.race([
      Promise.resolve().then(() => executor(attemptSignal.signal)),
      attemptSignal.abortPromise,
    ]);
  } catch (error) {
    if (attemptSignal.didTimeout()) {
      throw new FetchRequestTimeoutError(
        options?.timeoutMessage ?? 'The request timed out. Please try again.'
      );
    }
    throw error;
  } finally {
    attemptSignal.cleanup();
  }
}

function assertPureReadRequest(init?: PureReadRequestInit) {
  const method = getMethod(init);
  if (!PURE_READ_METHODS.has(method)) {
    throw new Error(
      `fetchPureReadWithOneRetry only permits GET or HEAD requests, received ${method}.`
    );
  }

  if (
    'body' in (init ?? {}) &&
    (init as RequestInit | undefined)?.body !== undefined
  ) {
    throw new Error('Pure read requests cannot include a body.');
  }
}

export function isRetryablePureReadResponseStatus(status: number) {
  return PURE_READ_RETRY_STATUSES.has(status);
}

/**
 * Runs an explicit command once. Commands may change server state, therefore
 * this function never retries after a timeout, transport failure, or response.
 */
export async function fetchCommandOnce(
  input: RequestInfo | URL,
  init: RequestInit,
  options?: RequestTimeoutOptions
): Promise<Response> {
  const method = getMethod(init);
  if (PURE_READ_METHODS.has(method)) {
    throw new Error(
      `fetchCommandOnce requires a state-changing HTTP method, received ${method}.`
    );
  }

  return fetchOnce(input, init, options);
}

/**
 * The sole retry policy for a request that has already been proven to be a
 * pure read. It retries at most once after a transient transport failure or
 * retryable response. Command callers must use executeRequestWithTimeout.
 */
export async function executePureReadWithOneRetry<T extends Response>(
  executor: (signal: AbortSignal) => Promise<T>,
  options?: PureReadFetchOptions
): Promise<T> {
  const retryStatuses = options?.retryStatuses ?? PURE_READ_RETRY_STATUSES;
  const retryDelayMs = options?.retryDelayMs ?? PURE_READ_RETRY_DELAY_MS;

  let firstResponse: T;
  try {
    firstResponse = await executeRequestWithTimeout(executor, options);
  } catch (firstError) {
    if (!isTransientNetworkError(firstError)) {
      throw firstError;
    }

    await sleep(retryDelayMs, options?.signal);
    return executeRequestWithTimeout(executor, options);
  }

  if (!retryStatuses.has(firstResponse.status)) {
    return firstResponse;
  }

  const retryAfterMs = parseRetryAfterMs(
    firstResponse.headers.get('Retry-After')
  );
  const requestedDelayMs = retryAfterMs ?? retryDelayMs;
  const maxRetryDelayMs = options?.maxRetryDelayMs;
  const delayMs =
    typeof maxRetryDelayMs === 'number' &&
    Number.isFinite(maxRetryDelayMs) &&
    maxRetryDelayMs >= 0
      ? Math.min(requestedDelayMs, maxRetryDelayMs)
      : requestedDelayMs;
  await sleep(delayMs, options?.signal);
  return executeRequestWithTimeout(executor, options);
}

/**
 * The sole browser retry policy: an explicitly pure GET/HEAD may repeat once.
 * It is intentionally unavailable to command callers.
 */
export async function fetchPureReadWithOneRetry(
  input: RequestInfo | URL,
  init?: PureReadRequestInit,
  options?: PureReadFetchOptions
): Promise<Response> {
  assertPureReadRequest(init);

  const retryStatuses = options?.retryStatuses ?? PURE_READ_RETRY_STATUSES;
  const signal = options?.signal ?? init?.signal ?? undefined;
  return executePureReadWithOneRetry(
    (attemptSignal) =>
      fetch(input, {
        ...init,
        signal: attemptSignal,
      }),
    {
      ...options,
      signal,
      retryStatuses,
    }
  );
}

export function isTransientNetworkRequestError(error: unknown): boolean {
  return isTransientNetworkError(error);
}
